import {put, takeEvery, call, select} from 'redux-saga/effects';
import {
    FETCH_CONSOLIDATED_FEEDBACK_REQUEST,
    FETCH_CONSOLIDATED_FEEDBACK_SUCCESS,
    FETCH_CONSOLIDATED_FEEDBACK_FAILURE,
    FETCH_CONSOLIDATED_FEEDBACK,
    POST_MANAGER_RATING,
    POST_MANAGER_RATING_REQUEST,
    POST_MANAGER_RATING_SUCCESS,
    POST_MANAGER_RATING_FAILURE,
    PUT_MANAGER_RATING,
    PUT_MANAGER_RATING_REQUEST,
    PUT_MANAGER_RATING_SUCCESS,
    PUT_MANAGER_RATING_FAILURE,
    DELETE_MANAGER_RATING_REQUEST,
    DELETE_MANAGER_RATING_SUCCESS,
    DELETE_MANAGER_RATING_FAILURE,
    DELETE_MANAGER_RATING,
    FETCH_READ_COMMENT_REQUEST,
    FETCH_READ_COMMENT_SUCCESS,
    FETCH_READ_COMMENT_FAILURE,
    FETCH_READ_COMMENT
} from './constants';
import Api, {ApiMethods} from 'feedback-system/middleware/api';
import {
    FetchConsolidatedFBAction,
    PostManagerRatingAction,
    PutManagerRatingAction,
    DeleteManagerRatingAction,
    ManagerReadCommentAction
} from './typedef';
import {feedbackData, resetData, readData} from './selectors';
import {getConsolidatedFeedback} from './utils';

function* fetchConsolidatedFB(feedbackInfo: FetchConsolidatedFBAction) {
    const {periodId} = feedbackInfo.payload;
    try {
        yield put({type: FETCH_CONSOLIDATED_FEEDBACK_REQUEST});

        const users = (yield call(Api.fetch, {
            endpoint: 'profiles/all',
            method: ApiMethods.GET
        })).responseData;

        const deletedUsers = (yield call(Api.fetch, {
            endpoint: 'profiles/deleted',
            method: ApiMethods.GET
        })).responseData;

        const submittedFeedback = (yield call(Api.fetch, {
            endpoint: `feedback/submitted/${periodId}`,
            method: ApiMethods.GET
        })).responseData;

        const consolidatedFeedback = (yield call(Api.fetch, {
            endpoint: `feedback/consolidated/${periodId}`,
            method: ApiMethods.GET
        })).responseData;

        const usersWithTeamLeaders = (yield call(Api.fetch, {
            endpoint: 'profiles/lead-info',
            method: ApiMethods.GET
        })).responseData;

        yield put({
            type: FETCH_CONSOLIDATED_FEEDBACK_SUCCESS,
            data: getConsolidatedFeedback({
                users,
                deletedUsers,
                submittedFeedback,
                consolidatedFeedback,
                usersWithTeamLeaders
            })
        });
    } catch (e) {
        yield put({type: FETCH_CONSOLIDATED_FEEDBACK_FAILURE});
    }
}

export function* fetchConsolidatedFBSaga() {
    yield takeEvery(FETCH_CONSOLIDATED_FEEDBACK, fetchConsolidatedFB);
}

function* postManagerRating(feedbackInfo: PostManagerRatingAction) {
    const {rating, personId, periodId} = feedbackInfo.payload.postPayload;
    const feedbackStateData = yield select(feedbackData);

    const query = {
        feedbackData: {
            RATING: rating,
            STATUS: 'CONSOLIDATED'
        },
        periodId,
        recipientId: personId
    };
    try {
        yield put({type: POST_MANAGER_RATING_REQUEST});

        const data = yield call(Api.fetch, {
            endpoint: 'feedback/consolidated',
            method: ApiMethods.POST,
            headers: {
                'Content-Type': 'application/json'
            },
            body: query
        });

        const args = {
            response: data.responseData,
            feedbackStateData,
            personId,
            rating
        };

        const newFeedbackData = resetData(args);

        yield put({
            type: POST_MANAGER_RATING_SUCCESS,
            data: newFeedbackData
        });
    } catch (e) {
        yield put({type: POST_MANAGER_RATING_FAILURE});
    }
}

export function* postManagerRatingSaga() {
    yield takeEvery(POST_MANAGER_RATING, postManagerRating);
}

function* putManagerRating(feedbackInfo: PutManagerRatingAction) {
    const {rating, personId, feedbackId, periodId} = feedbackInfo.payload.putPayload;
    const feedbackStateData = yield select(feedbackData);

    const query = {
        feedbackData: {
            RATING: rating,
            STATUS: 'CONSOLIDATED'
        },
        periodId,
        recipientId: personId
    };
    try {
        yield put({type: PUT_MANAGER_RATING_REQUEST});

        const data = yield call(Api.fetch, {
            endpoint: `feedback/consolidated/${feedbackId}`,
            method: ApiMethods.PUT,
            headers: {
                'Content-Type': 'application/json'
            },
            body: query
        });

        const args = {
            response: data.responseData,
            feedbackStateData,
            personId,
            rating
        };
        const newFeedbackData = resetData(args);

        yield put({
            type: PUT_MANAGER_RATING_SUCCESS,
            data: newFeedbackData
        });
    } catch (e) {
        yield put({type: PUT_MANAGER_RATING_FAILURE});
    }
}

export function* putManagerRatingSaga() {
    yield takeEvery(PUT_MANAGER_RATING, putManagerRating);
}

function* deleteManagerRating(feedbackInfo: DeleteManagerRatingAction) {
    const {personId, feedbackId} = feedbackInfo.payload;
    const feedbackStateData = yield select(feedbackData);
    const rating = null;

    try {
        yield put({type: DELETE_MANAGER_RATING_REQUEST});

        yield call(Api.fetch, {
            endpoint: `feedback/manager/${feedbackId}`,
            method: ApiMethods.DELETE
        });

        const args = {
            response: null,
            feedbackStateData,
            personId,
            rating
        };
        const newFeedbackData = resetData(args);

        yield put({
            type: DELETE_MANAGER_RATING_SUCCESS,
            data: newFeedbackData
        });
    } catch (e) {
        yield put({type: DELETE_MANAGER_RATING_FAILURE});
    }
}

export function* deleteManagerRatingSaga() {
    yield takeEvery(DELETE_MANAGER_RATING, deleteManagerRating);
}

function* managerReadComment(feedbackInfo: ManagerReadCommentAction) {
    const {feedbackId} = feedbackInfo.payload;
    try {
        yield put({type: FETCH_READ_COMMENT_REQUEST});
        yield call(Api.fetch, {
            endpoint: `feedback/last-view/${feedbackId}`,
            method: ApiMethods.PUT
        });

        const feedbackStateData = yield select(feedbackData);

        const args = {
            type: 'comment',
            feedbackStateData,
            feedbackId
        };

        const newFeedbackData = readData(args);

        yield put({
            type: FETCH_READ_COMMENT_SUCCESS,
            data: newFeedbackData
        });
    } catch (e) {
        yield put({type: FETCH_READ_COMMENT_FAILURE});
    }
}

export function* managerReadCommentSaga() {
    yield takeEvery(FETCH_READ_COMMENT, managerReadComment);
}
